import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemeChoice = "system" | "light" | "dark";

interface ThemeState {
  choice: ThemeChoice;
  resolved: "light" | "dark";
  setChoice: (choice: ThemeChoice) => void;
}

const media = typeof window !== "undefined" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

function resolve(choice: ThemeChoice): "light" | "dark" {
  if (choice === "system") return media?.matches ? "dark" : "light";
  return choice;
}

/** Writes the scheme onto <html> so the CSS tokens switch, and tints the iOS status bar to match. */
function apply(choice: ThemeChoice): "light" | "dark" {
  const resolved = resolve(choice);
  const root = document.documentElement;
  if (choice === "system") delete root.dataset.theme;
  else root.dataset.theme = choice;
  root.style.colorScheme = resolved;
  const desk = getComputedStyle(root).getPropertyValue("--color-desk").trim();
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = desk || (resolved === "dark" ? "#1e2124" : "#f2f3f1");
  return resolved;
}

export const useTheme = create<ThemeState>()(
  persist(
    (set) => ({
      choice: "system",
      resolved: resolve("system"),
      setChoice: (choice) => set({ choice, resolved: apply(choice) }),
    }),
    {
      name: "studio-theme",
      partialize: (s) => ({ choice: s.choice }),
      onRehydrateStorage: () => (state) => {
        if (state) state.resolved = apply(state.choice);
      },
    },
  ),
);

/** Follow the phone or computer when the choice is "system". */
export function watchSystemTheme(): () => void {
  apply(useTheme.getState().choice);
  const onChange = () => {
    const { choice } = useTheme.getState();
    if (choice === "system") useTheme.setState({ resolved: apply("system") });
  };
  media?.addEventListener("change", onChange);
  return () => media?.removeEventListener("change", onChange);
}

/** Current value of a CSS token, for canvas-drawn things (waveforms) that cannot use classes. */
export function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
